import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Box,
  Paper,
  Tabs,
  Tab,
  Grid,
  Card,
  CardContent,
  Alert,
  useTheme
} from '@mui/material';
import { Icon } from '@iconify/react';
import { SessionList, SessionFilter } from '../../components/ocpi/sessions';
import { useFetchOnDemand } from 'src/hooks/useFetch.hook';
import { formatOcpiCurrency } from '../../utils/formatTime';

const OCPISessions= () => {
  const [activeTab, setActiveTab] = useState(0);
  const [filters, setFilters] = useState({
    search: '',
    status: 'All Status',
    partnerId: 'all',
    locationId: 'all',
    currency: 'all',
    dateRange: {
      start: '',
      end: ''
    },
    minEnergy: '',
    maxEnergy: '',
    minCost: '',
    maxCost: ''
  });
  // Filtered data hooks
  const { fetchData: fetchFilteredOngoingSessions, data: filteredOngoingSessions, isLoading: isFilteredOngoingLoading } = useFetchOnDemand("ongoingSessions", false, false);
  const { fetchData: fetchFilteredCompletedSessions, data: filteredCompletedSessions, isLoading: isFilteredCompletedLoading } = useFetchOnDemand("completedSessions", false, false);
  
  const theme = useTheme();

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
    handleApplyFilters(newValue);
  };

  const handleFiltersChange = (newFilters) => {
    setFilters(newFilters);
  };

  const handleApplyFilters = (newValue = -1) => {
    // Build filter parameters
    const params = {
      limit: 50,
      offset: 0
    };

    // Add search parameter
    if (filters.search && filters.search.trim()) {
      params.search = filters.search.trim();
    }

    // Add status filter
    if (filters.status && filters.status !== 'all') {
      params.status = filters.status;
    }

    // Add partner filter
    if (filters.partnerId && filters.partnerId !== 'all') {
      params.partnerId = filters.partnerId;
    }

    // Add location filter
    if (filters.locationId && filters.locationId !== 'all') {
      params.locationId = filters.locationId;
    }

    // Add currency filter
    if (filters.currency && filters.currency !== 'all') {
      params.currency = filters.currency;
    }

    // Add date range filters
    if (filters.dateRange?.start) {
      params.startDate = filters.dateRange.start;
    }
    if (filters.dateRange?.end) {
      params.endDate = filters.dateRange.end;
    }

    // Add energy range filters
    if (filters.minEnergy) {
      params.minEnergy = filters.minEnergy;
    }
    if (filters.maxEnergy) {
      params.maxEnergy = filters.maxEnergy;
    }

    // Add cost range filters
    if (filters.minCost) {
      params.minCost = filters.minCost;
    }
    if (filters.maxCost) {
      params.maxCost = filters.maxCost;
    }

    // Apply filters to the current tab's data
    const tab = newValue !== -1 ? newValue : activeTab;
    console.log(tab);
    if (tab === 0) {
      fetchFilteredOngoingSessions(params);
    } else {
      fetchFilteredCompletedSessions(params);
    }
  };

  useEffect(() => {
    handleApplyFilters();
    handleApplyFilters(1);
  }, []);

  const handleResetFilters = () => {
    setFilters({
      search: '',
      status: 'All Status',
      partnerId: 'all',
      locationId: 'all',
      currency: 'all',
      dateRange: {
        start: '',
        end: ''
      },
      minEnergy: '',
      maxEnergy: '',
      minCost: '',
      maxCost: ''
    });
  };

  const getTotalStats = () => {
    if (filteredOngoingSessions && filteredCompletedSessions) {
      const totalOngoing = filteredOngoingSessions.length;
      const totalCompleted = filteredCompletedSessions.length;
      const totalEnergy = filteredOngoingSessions.reduce((sum, session) => sum + (Number(session.kwhConsumed) || 0), 0) +
                        filteredCompletedSessions.reduce((sum, session) => sum + (Number(session.kwhConsumed) || 0), 0);
      
      // Calculate total revenue grouped by currency
      const revenueByCurrency = filteredCompletedSessions.reduce((acc, session) => {
        const amount = Number(session.totalCost?.incl_vat) || 0;
        const currency = session.currency || 'USD';
        
        if (acc[currency]) {
          acc[currency] += amount;
        } else {
          acc[currency] = amount;
        }
        return acc;
      }, {});
      
      return { totalOngoing, totalCompleted, totalEnergy, revenueByCurrency };
    }

    return { totalOngoing: 0, totalCompleted: 0, totalEnergy: 0, revenueByCurrency: {} };
  };

  const stats = getTotalStats();

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      {/* Page Header */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" component="h1" sx={{ fontWeight: 'bold', mb: 2 }}>
          OCPI Sessions Management (CDRs)
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Monitor and manage electric vehicle charging sessions and Charge Detail Records (CDRs) across all connected charging stations
        </Typography>
      </Box>

      {/* Statistics Cards */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ py: 2, px: 2, height: '100%' }}>
            <CardContent sx={{ p: '16px !important', display: 'flex', flexDirection: 'column', height: '100%' }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
                  Active Sessions
                </Typography>
                <Icon icon="eva:radio-button-on-fill" color={theme.palette.primary.main} fontSize={24} />
              </Box>
              <Typography variant="h4" sx={{ fontWeight: 'bold', color: '#4caf50', mt: 'auto' }}>
                {stats.totalOngoing}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ py: 2, px: 2, height: '100%' }}>
            <CardContent sx={{ p: '16px !important', display: 'flex', flexDirection: 'column', height: '100%' }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
                  Completed Sessions
                </Typography>
                <Icon icon="eva:checkmark-circle-2-fill" fontSize={24} color={theme.palette.info.main} />
              </Box>
              <Typography variant="h4" sx={{ fontWeight: 'bold', color: theme.palette.info.main, mt: 'auto' }}>
                {stats.totalCompleted}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ py: 2, px: 2, height: '100%' }}>
            <CardContent sx={{ p: '16px !important', display: 'flex', flexDirection: 'column', height: '100%' }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
                  Total kWh
                </Typography>
                <Icon icon="eva:flash-fill" color={theme.palette.warning.main} fontSize={24} />
              </Box>
              <Typography variant="h4" sx={{ fontWeight: 'bold', color: theme.palette.warning.main, mt: 'auto' }}>
                {stats.totalEnergy.toFixed(1)}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ py: 2, px: 2, height: '100%' }}>
            <CardContent sx={{ p: '16px !important', display: 'flex', flexDirection: 'column', height: '100%' }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
                  Total Revenue
                </Typography>
                <Icon icon="eva:credit-card-fill" color={theme.palette.success.main} fontSize={24} />
              </Box>
              <Box sx={{ mt: 'auto' }}>
                {Object.keys(stats.revenueByCurrency).length > 0 ? (
                  Object.entries(stats.revenueByCurrency).map(([currency, amount], index) => (
                    <Typography 
                      key={currency} 
                      variant="h4" 
                      sx={{ 
                        fontWeight: 'bold', 
                        color: theme.palette.success.main,
                        mb: index < Object.keys(stats.revenueByCurrency).length - 1 ? 0.5 : 0
                      }}
                    >
                      {formatOcpiCurrency(amount, currency)}
                    </Typography>
                  ))
                ) : (
                  <Typography variant="h4" sx={{ fontWeight: 'bold', color: theme.palette.success.main }}>
                    $0.00
                  </Typography>
                )}
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Session Filter */}
      <SessionFilter
        filters={filters}
        onFiltersChange={handleFiltersChange}
        onApply={handleApplyFilters}
        onReset={handleResetFilters}
        loading={isFilteredOngoingLoading || isFilteredCompletedLoading}
      />

      {/* Tabs for Session Types */}
      <Paper sx={{ mb: 4, overflow: 'hidden' }}>
        <Tabs 
          value={activeTab} 
          onChange={handleTabChange}
          variant="fullWidth"
          sx={{
            borderBottom: 1,
            borderColor: 'divider',
            backgroundColor: 'background.paper',
            '& .MuiTab-root': {
              textTransform: 'none',
              fontWeight: 500,
              fontSize: '0.95rem',
              minHeight: 48,
              color: 'text.secondary',
              '&.Mui-selected': {
                color: 'primary.main',
                fontWeight: 600,
              }
            },
            '& .MuiTabs-indicator': {
              height: 3,
              borderRadius: '3px 3px 0 0'
            }
          }}
        >
          <Tab 
            label={
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Icon icon="eva:radio-button-on-fill" color={theme.palette.primary.main} />
                Active Sessions ({(filteredOngoingSessions || []).length})
              </Box>
            }
          />
          <Tab 
            label={
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Icon icon="eva:file-text-fill" color={theme.palette.info.main} />
                CDR Records ({(filteredCompletedSessions || []).length})
              </Box>
            }
          />
        </Tabs>
      </Paper>

      {/* Tab Content */}
      {activeTab === 0 && (
        <SessionList
          title="Active Sessions"
          sessions={filteredOngoingSessions}
          isOngoing={true}
          emptyMessage="No active charging sessions at the moment"
          icon="eva:radio-button-on-fill"
          loading={isFilteredOngoingLoading || isFilteredOngoingLoading}
          refetch={handleApplyFilters}
          showCdrDetails={false}
          changeActiveTab={setActiveTab}
        />
      )}

      {activeTab === 1 && (
        <SessionList
          title="CDR Records"
          sessions={filteredCompletedSessions}
          isOngoing={false}
          emptyMessage="No CDR records found"
          icon="eva:file-text-fill"
          loading={isFilteredCompletedLoading}
          refetch={handleApplyFilters}
          showCdrDetails={true}
        />
      )}

      {/* Additional Information */}
      <Paper sx={{ p: 3, mt: 4 }}>
        <Typography variant="h6" sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
          <Icon icon="eva:info-outline" />
          About OCPI Sessions & CDR Records
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          OCPI (Open Charge Point Interface) sessions represent charging transactions between electric vehicles 
          and charging stations. Active sessions show real-time charging progress, while CDR (Charge Detail Record) 
          records contain comprehensive billing and transaction details after session completion.
        </Typography>
        <Alert severity="info" sx={{ mt: 2 }}>
          <Typography variant="body2">
            <strong>Note:</strong> Active sessions are updated in real-time. CDR records are generated after 
            session completion and contain detailed billing information, token details, and location data for 
            accounting and audit purposes.
          </Typography>
        </Alert>
      </Paper>
    </Container>
  );
};

export default OCPISessions;